import { isSimpleUiPack } from "@/lib/uiPack";

/** Route → required permission slug (first match wins). */
const ROUTE_RULES = [
  { prefix: "/waiter", permission: "waiter.view" },
  { prefix: "/pos", permission: "pos.view" },
  { prefix: "/tables", permission: "pos.view" },
  { prefix: "/delivery", permission: "delivery.view" },
  { prefix: "/masters", permission: "masters.view" },
  { prefix: "/accounts", permission: "accounts.view" },
  { prefix: "/reports", permission: "reports.view" },
  { prefix: "/admin", permission: "administration.view" },
] as const;

const DASHBOARD_PERMS = [
  "reports.view",
  "administration.view",
  "accounts.view",
  "masters.view",
] as const;

export type FeatureUser = {
  features?: Record<string, boolean>;
  company?: { features?: Record<string, boolean>; ui_pack?: string };
} | null | undefined;

export function can(permissions: string[] | undefined, slug: string): boolean {
  if (!slug) return true;
  return (permissions ?? []).includes(slug);
}

export function canAccessDashboard(permissions: string[] | undefined): boolean {
  return DASHBOARD_PERMS.some((p) => can(permissions, p));
}

export function companyFeatures(user: FeatureUser): Record<string, boolean> {
  return user?.features || user?.company?.features || {};
}

export function isClassicUiPack(user?: FeatureUser): boolean {
  return !isSimpleUiPack(user?.company?.ui_pack);
}

/** Console premium: Online store / WhatsApp inbox — Classic UI only for now. */
export function isOnlineFeatureEnabled(user?: FeatureUser): boolean {
  if (!isClassicUiPack(user)) return false;
  return Boolean(companyFeatures(user).online);
}

/** Multi-branch switching / comparison — premium; Classic UI only for now. */
export function isMultiBranchFeatureEnabled(user?: FeatureUser): boolean {
  if (!isClassicUiPack(user)) return false;
  const features = companyFeatures(user);
  if (Object.prototype.hasOwnProperty.call(features, "multi_branch")) {
    return Boolean(features.multi_branch);
  }
  return false;
}

/**
 * Waiter ordering UI is for dedicated waiter staff only — not admin/manager/cashier
 * users who happen to have waiter.view via the owner role.
 */
/** KDS / kitchen order board — kitchen staff, POS supervisors, and back-office admins. */
export function canViewKitchenOrders(permissions: string[] = []): boolean {
  if (can(permissions, "kitchen-display.view")) return true;
  if (canAccessDashboard(permissions)) return true;
  if (can(permissions, "pos.view")) return true;
  return false;
}

export function canUseWaiterScreen(permissions: string[] = []): boolean {
  if (!can(permissions, "waiter.view")) return false;
  if (canAccessDashboard(permissions)) return false;
  if (can(permissions, "administration.view")) return false;
  if (permissions.some((p) => p.startsWith("pos."))) return false;
  return true;
}

export function canAccessPath(pathname: string, permissions: string[] | undefined, user?: FeatureUser): boolean {
  if (pathname === "/" || pathname === "") {
    // Simple pack: home is the module launcher for every role.
    if (isSimpleUiPack(user?.company?.ui_pack)) return true;
    return canAccessDashboard(permissions);
  }

  if (pathname === "/waiter" || pathname.startsWith("/waiter/")) {
    return canUseWaiterScreen(permissions ?? []);
  }

  if (pathname === "/kds" || pathname.startsWith("/kds/")) {
    return canViewKitchenOrders(permissions ?? []);
  }

  if (pathname === "/online" || pathname.startsWith("/online/")) {
    if (!isOnlineFeatureEnabled(user)) return false;
  }

  if (pathname === "/admin/online-store" || pathname.startsWith("/admin/online-store/")) {
    if (!isOnlineFeatureEnabled(user)) return false;
  }

  if (pathname === "/reports/bill-history" || pathname.startsWith("/reports/bill-history/")) {
    return can(permissions, "reports.view") || can(permissions, "pos.view");
  }

  if (pathname === "/reports/branch-comparison" || pathname.startsWith("/reports/branch-comparison/")) {
    if (!isMultiBranchFeatureEnabled(user)) return false;
  }

  for (const rule of ROUTE_RULES) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      return can(permissions, rule.permission);
    }
  }

  return true;
}

/** Counter / waiter staff without back-office modules. */
export function isMinimalStaff(permissions: string[] = []): boolean {
  const hasBackOffice = permissions.some(
    (p) =>
      p.startsWith("administration.")
      || p.startsWith("accounts.")
      || p.startsWith("reports.")
      || p.startsWith("masters."),
  );
  if (hasBackOffice) return false;
  return permissions.some(
    (p) => p.startsWith("pos.") || p.startsWith("waiter.") || p.startsWith("kitchen-display."),
  );
}

export function getDefaultRoute(permissions: string[] = [], user?: FeatureUser): string {
  if (isSimpleUiPack(user?.company?.ui_pack)) return "/";
  if (canAccessDashboard(permissions)) return "/";

  const hasPos = permissions.some((p) => p.startsWith("pos."));
  const hasKds = permissions.some((p) => p.startsWith("kitchen-display."));

  if (hasPos) return "/pos";
  if (canUseWaiterScreen(permissions)) return "/waiter";
  if (hasKds) return "/kds";
  return "/login";
}

/** After login, only return to the prior page for front-line staff (not back-office). */
export function getPostLoginRoute(
  permissions: string[] = [],
  fromPath?: string,
  user?: FeatureUser,
): string {
  const home = getDefaultRoute(permissions, user);
  if (isSimpleUiPack(user?.company?.ui_pack)) {
    return home;
  }
  if (
    fromPath
    && fromPath !== "/login"
    && canAccessPath(fromPath, permissions, user)
    && !canAccessDashboard(permissions)
  ) {
    return fromPath;
  }
  return home;
}

export interface MenuItemWithPermission {
  permission?: string;
  accessCheck?: (permissions: string[]) => boolean;
  to?: string;
  premium?: boolean;
  [key: string]: unknown;
}

function isOnlineMenuPath(to?: string): boolean {
  if (!to) return false;
  return (
    to === "/online/orders"
    || to.startsWith("/online/")
    || to === "/admin/online-store"
    || to.startsWith("/admin/online-store/")
  );
}

export function filterMenuItems<T extends MenuItemWithPermission>(
  items: T[],
  permissions: string[],
  user?: FeatureUser,
): T[] {
  return items.filter((item) => {
    if (item.to === "/waiter" && !canUseWaiterScreen(permissions)) return false;
    if (
      item.to === "/reports/branch-comparison"
      || item.to?.startsWith("/reports/branch-comparison/")
    ) {
      if (!isMultiBranchFeatureEnabled(user)) return false;
    }
    if (isOnlineMenuPath(item.to) && !isOnlineFeatureEnabled(user)) return false;
    if (item.accessCheck) return item.accessCheck(permissions);
    return !item.permission || can(permissions, item.permission);
  });
}

export function filterMenus<T extends { items: MenuItemWithPermission[] }>(
  menus: T[],
  permissions: string[],
  user?: FeatureUser,
): T[] {
  return menus
    .map((menu) => ({
      ...menu,
      items: filterMenuItems(menu.items, permissions, user),
    }))
    .filter((menu) => menu.items.length > 0);
}
